import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Alert, Button, Card, Input, Radio, Select, Space, Typography } from "antd";
import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import {
    DateRangePicker as ZhDateRangePicker,
    Dialog as ZhDialog,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { clearDaiBanState, getDaiBanState } from "@/utils";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { useUserStore } from "@/views/stores";
import type { FundingCluesForm, FundingCluesQueryParams, FundingCluesVo, UserInfo } from "@zhihaoscm/entity";
import { FUNDING_CLUES_STATE, FUNDING_CLUES_TYPE, REGISTER_SOURCE } from "@zhihaoscm/meta";

export interface FundingCluesListResult {
    list: FundingCluesVo[];
    total: number;
}

/** Vue 页面实际使用的扩展筛选参数，补充服务类型未声明的处理人字段。 */
export interface FundingCluesPageQuery extends FundingCluesQueryParams {
    cluesSource?: number;
    handlerId?: number;
}

export interface FundingCluesPageProps {
    loadClues?: (query: FundingCluesPageQuery) => Promise<FundingCluesListResult>;
    loadHandlers?: (keyword: string) => Promise<Array<{ value: number; label: string }>>;
    handleClue?: (id: number | string, payload: FundingCluesForm) => Promise<unknown>;
    permissions?: string[];
}

const DEFAULT_QUERY: FundingCluesQueryParams = {
    page: 1,
    size: 10,
    sortKey: "created_time",
    sortOrder: "desc"
};

// 资金线索来源沿用旧 Vue 页面 REGISTER_SOURCE 的两项闭合集合。
const typeOptions = FUNDING_CLUES_TYPE.options.map(item => ({ value: item.value, label: item.label }));
const stateOptions = FUNDING_CLUES_STATE.options.map(item => ({ value: item.value, label: item.label }));

/** 沿用 Vue FundingCluesService.paging 的资金线索分页接口。 */
export async function loadFundingClues(query: FundingCluesPageQuery): Promise<FundingCluesListResult> {
    const response = await request.service.get<FundingCluesListResult>("/admin/funding/clues/paging", {
        params: query
    });
    return response.data;
}

/** 沿用 Vue FundingCluesService.handle 的资金线索处理接口。 */
export async function handleFundingClue(id: number | string, payload: FundingCluesForm): Promise<unknown> {
    const response = await request.service.put(`/admin/funding/clues/${id}`, payload);
    return response.data;
}

/** 沿用 Vue userService.userSelector 的处理人远程选择接口。 */
export async function loadFundingClueHandlers(keyword: string): Promise<Array<{ value: number; label: string }>> {
    const response = await request.service.get<UserInfo[]>("/admin/user/selector", { params: { name: keyword } });
    return (response.data || []).slice(0, 50).map(item => ({ value: item.id, label: item.name }));
}

/** 读取我的任务跳转缓存，复现 Vue 资金线索列表的状态回填。 */
function getInitialFundingCluesQuery(): FundingCluesQueryParams {
    const states = getDaiBanState("states");
    return states.length ? { ...DEFAULT_QUERY, states } : DEFAULT_QUERY;
}

/** 沿用 Vue dateTimeFormat 的分钟级申请时间展示。 */
function formatDateTime(value?: string): string {
    return value ? value.replace("T", " ").slice(0, 16) : "--";
}

/** 将 Vue 客户 JSON 字段映射为列表中的反馈账号文案。 */
function getCustomerLabel(row: FundingCluesVo): string {
    const customer = row.fundingClues.customerEnterprise;
    return (
        [customer?.institutionName || customer?.realName, customer?.mobile, customer?.code, customer?.appTypeName]
            .filter(Boolean)
            .join(" | ") || "--"
    );
}

/** 沿用 Vue 金额格式化规则，列表不显示小数位。 */
function formatAmount(value: number | undefined): string {
    const amount = Number(value);
    return amount ? amount.toLocaleString("en-US", { maximumFractionDigits: 0 }) : "--";
}

/** 用户申请资金列表，保留 Vue 默认排序、筛选字段、枚举和处理入口。 */
export function FundingCluesPage({
    loadClues = loadFundingClues,
    loadHandlers = loadFundingClueHandlers,
    handleClue = handleFundingClue,
    permissions
}: FundingCluesPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    // 沿用 Vue 全局封禁态，资金线索处理入口保留但禁止触发。
    const banButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions ?? storePermissions;
    const [query, setQuery] = useState<FundingCluesQueryParams>(getInitialFundingCluesQuery);
    const [draft, setDraft] = useState<{
        keyWord: string;
        cluesSource?: number;
        types: number[];
        states: number[];
        handlerId: string;
        beginDate: string;
        endDate: string;
    }>(() => ({
        keyWord: "",
        types: [],
        // 与 Vue 的 init-param/defaultValue 保持一致，避免点击查询时丢失待办状态。
        states: getDaiBanState("states"),
        handlerId: "",
        beginDate: "",
        endDate: ""
    }));
    const [items, setItems] = useState<FundingCluesVo[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [currentId, setCurrentId] = useState<number | string | null>(null);
    const [handleState, setHandleState] = useState<number>(2);
    const [remark, setRemark] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [handlerOptions, setHandlerOptions] = useState<Array<{ value: number; label: string }>>([]);

    const canManage = currentPermissions.includes("ROLE_FUNDS_W");

    /** 将列表筛选控件转换为 Vue FundingCluesQueryParams。 */
    const submitFilters = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const nextQuery: FundingCluesPageQuery = { ...DEFAULT_QUERY };
        if (draft.keyWord.trim()) nextQuery.keyWord = draft.keyWord.trim();
        if (draft.cluesSource !== undefined) nextQuery.cluesSource = draft.cluesSource;
        if (draft.types.length > 0) nextQuery.types = draft.types;
        if (draft.states.length > 0) nextQuery.states = draft.states;
        if (draft.handlerId.trim()) nextQuery.handlerId = Number(draft.handlerId.trim());
        if (draft.beginDate && draft.endDate) {
            nextQuery.beginTime = `${draft.beginDate}T00:00:00`;
            nextQuery.endTime = `${draft.endDate}T23:59:59`;
        }
        setErrorMessage(null);
        setQuery(nextQuery);
    };

    /** 清空全部资金线索筛选项，并恢复旧 Vue ProTable 的默认分页和排序。 */
    const resetFilters = () => {
        clearDaiBanState([], "states");
        setDraft({
            keyWord: "",
            cluesSource: undefined,
            types: [],
            states: [],
            handlerId: "",
            beginDate: "",
            endDate: ""
        });
        setErrorMessage(null);
        setQuery({ ...DEFAULT_QUERY });
    };

    /** 提交 Vue 资金线索处理表单并刷新当前列表。 */
    const submitHandle = async () => {
        if (currentId == null) return;
        if (!remark.trim()) {
            setErrorMessage("请输入备注");
            return;
        }
        setSubmitting(true);
        try {
            await handleClue(currentId, { state: handleState, remark: remark.trim() });
            setCurrentId(null);
            setRemark("");
            const result = await loadClues(query);
            setItems(result.list || []);
            setTotal(Number(result.total) || 0);
            setErrorMessage(null);
        } catch {
            setErrorMessage("资金申请处理失败");
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        let disposed = false;
        void loadClues(query)
            .then(result => {
                if (!disposed) {
                    setItems(result.list || []);
                    setTotal(Number(result.total) || 0);
                    setErrorMessage(null);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setItems([]);
                    setTotal(0);
                    setErrorMessage("用户申请资金列表加载失败");
                }
            });
        return () => {
            disposed = true;
        };
    }, [loadClues, query]);

    /** 将 zhihao-ui-react 申请时间排序转换为 Vue created_time 参数。 */
    const handleSortChange = ({ order }: { prop?: string; order: "ascending" | "descending" | null }) => {
        setQuery(current => ({
            ...current,
            page: 1,
            sortKey: order ? "created_time" : undefined,
            sortOrder: order === "ascending" ? "asc" : order === "descending" ? "desc" : undefined
        }));
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 资金线索表格的字段、排序和处理插槽。
    const columns: TableColumnProps<FundingCluesVo>[] = [
        {
            label: "序号",
            width: 60,
            key: "id",
            prop: "fundingClues",
            render: ({ row }) => (
                <Link to={`/userOperation/fundingClues/detail?id=${row.fundingClues.id}`}>{row.fundingClues.id}</Link>
            )
        },
        {
            label: "反馈账号",
            minWidth: 260,
            key: "customer",
            prop: "fundingClues",
            render: ({ row }) => getCustomerLabel(row)
        },
        {
            label: "线索来源",
            width: 106,
            key: "cluesSource",
            prop: "fundingClues",
            render: ({ row }) => REGISTER_SOURCE.getIntroByCode(row.fundingClues.cluesSource) || "--"
        },
        {
            label: "资金需求类型",
            width: 129,
            key: "type",
            prop: "fundingClues",
            render: ({ row }) => FUNDING_CLUES_TYPE.getIntroByCode(row.fundingClues.type) || "--"
        },
        {
            label: "预计资金需求（元）",
            width: 155,
            key: "fundingAmount",
            prop: "fundingClues",
            render: ({ row }) => formatAmount(row.fundingClues.fundingAmount)
        },
        {
            label: "姓名",
            width: 100,
            key: "name",
            prop: "fundingClues",
            render: ({ row }) => row.fundingClues.name || "--"
        },
        {
            label: "电话",
            width: 119,
            key: "mobile",
            prop: "fundingClues",
            render: ({ row }) => row.fundingClues.mobile || "--"
        },
        {
            label: "公司名称",
            width: 101,
            key: "companyName",
            prop: "fundingClues",
            render: ({ row }) => row.fundingClues.companyName || "--"
        },
        {
            label: "申请时间",
            width: 146,
            prop: "fundingClues",
            key: "createdTime",
            sortable: true,
            render: ({ row }) => formatDateTime(row.fundingClues.createdTime)
        },
        {
            label: "状态",
            width: 78,
            key: "state",
            prop: "fundingClues",
            render: ({ row }) => FUNDING_CLUES_STATE.getIntroByCode(row.fundingClues.state) || "--"
        },
        {
            label: "操作",
            fixed: "right",
            width: 70,
            key: "operation",
            prop: "fundingClues",
            className: "admin-table-operation-column compact",
            labelClassName: "admin-table-operation-column compact",
            render: ({ row }) => {
                if (!canManage) return null;
                return (
                    <Space>
                        {Number(row.fundingClues.state) !== 3 ? (
                            <Button
                                aria-label="处理"
                                disabled={banButton}
                                onClick={() => {
                                    setCurrentId(row.fundingClues.id);
                                    setHandleState(2);
                                    setRemark("");
                                    setErrorMessage(null);
                                }}
                            >
                                处理
                            </Button>
                        ) : null}
                    </Space>
                );
            }
        }
    ];

    return (
        <section aria-label="funding-clues-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<FundingCluesVo>
                    data={items}
                    columns={normalizeOperationColumns(columns)}
                    emptyText="暂无用户申请资金记录"
                    pagination={false}
                    rowKey={row => row.fundingClues.id}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                用户申请资金
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <form className="admin-table-filter-form mb-4" onSubmit={submitFilters}>
                                <label>
                                    反馈账号或编号
                                    <Input
                                        className="funding-clues-keyword-filter"
                                        aria-label="反馈账号或编号"
                                        maxLength={32}
                                        placeholder="请输入反馈账号或编号搜索"
                                        value={draft.keyWord}
                                        onChange={event =>
                                            setDraft(current => ({ ...current, keyWord: event.target.value }))
                                        }
                                    />
                                </label>
                                <label>
                                    资金需求类型
                                    <Select
                                        className="funding-clues-type-filter"
                                        aria-label="资金需求类型"
                                        mode="multiple"
                                        options={typeOptions}
                                        placeholder="请选择资金需求类型"
                                        value={draft.types}
                                        onChange={value => setDraft(current => ({ ...current, types: value }))}
                                    />
                                </label>
                                <label>
                                    处理人
                                    <Select
                                        className="funding-clues-handler-filter"
                                        aria-label="处理人"
                                        filterOption={false}
                                        options={handlerOptions}
                                        placeholder="请选择处理人"
                                        showSearch
                                        value={draft.handlerId ? Number(draft.handlerId) : undefined}
                                        onChange={value =>
                                            setDraft(current => ({ ...current, handlerId: String(value || "") }))
                                        }
                                        onSearch={keyword => {
                                            if (!keyword.trim()) return;
                                            void loadHandlers(keyword.trim())
                                                .then(setHandlerOptions)
                                                .catch(() => setHandlerOptions([]));
                                        }}
                                    />
                                </label>
                                <label>
                                    状态
                                    <Select
                                        className="funding-clues-state-filter"
                                        aria-label="状态"
                                        mode="multiple"
                                        options={stateOptions}
                                        placeholder="请选择状态"
                                        value={draft.states}
                                        onChange={value => {
                                            clearDaiBanState(value, "states");
                                            setDraft(current => ({ ...current, states: value }));
                                        }}
                                    />
                                </label>
                                <ZhDateRangePicker
                                    type="daterange"
                                    format="YYYY-MM-DD"
                                    valueFormat="YYYY-MM-DD"
                                    rangeSeparator="-"
                                    startPlaceholder="申请开始日期"
                                    endPlaceholder="申请结束日期"
                                    value={[draft.beginDate, draft.endDate]}
                                    onChange={value =>
                                        setDraft(current => ({
                                            ...current,
                                            beginDate: String(value?.[0] || ""),
                                            endDate: String(value?.[1] || "")
                                        }))
                                    }
                                />
                                <Button
                                    aria-label="查询资金申请"
                                    htmlType="submit"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                >
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置资金申请"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    onClick={resetFilters}
                                />
                            </form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                className="mt-4 self-end"
                                current={query.page}
                                pageSize={query.size}
                                showSizeChanger
                                total={total}
                                onChange={(page, size) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: size !== current.size ? 1 : page,
                                        size
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
            <ZhDialog
                alignCenter
                modalClass="user-operation-funding-dialog"
                open={currentId !== null}
                title="处理"
                width={544}
                onCancel={() => setCurrentId(null)}
                footer={
                    <>
                        <Button onClick={() => setCurrentId(null)}>取消</Button>
                        <Button
                            aria-label="确认处理"
                            loading={submitting}
                            onClick={() => void submitHandle()}
                            type="primary"
                        >
                            确认
                        </Button>
                    </>
                }
            >
                <div className="user-operation-action-dialog-form">
                    <div className="user-operation-action-dialog-form-item user-operation-action-dialog-form-item--funding">
                        <span className="user-operation-action-dialog-form-label">是否处理</span>
                        <span className="user-operation-action-dialog-form-control">
                            <Radio.Group
                                aria-label="是否处理"
                                className="user-operation-action-dialog-radio-group"
                                value={handleState}
                                onChange={event => setHandleState(event.target.value)}
                            >
                                <Radio value={2}>处理中</Radio>
                                <Radio value={3}>已处理</Radio>
                            </Radio.Group>
                        </span>
                    </div>
                    <label className="user-operation-action-dialog-form-item user-operation-action-dialog-form-item--funding">
                        <span className="user-operation-action-dialog-form-label">
                            <span className="user-operation-action-dialog-required">*</span>
                            备注
                        </span>
                        <span className="user-operation-action-dialog-form-control">
                            <Input.TextArea
                                aria-label="备注"
                                className="user-operation-action-dialog-textarea"
                                maxLength={200}
                                placeholder="请输入备注"
                                rows={3}
                                showCount
                                value={remark}
                                onChange={event => setRemark(event.target.value)}
                            />
                        </span>
                    </label>
                </div>
            </ZhDialog>
        </section>
    );
}

export default FundingCluesPage;
